/**
 * Legacy six-face dimension -> final 2D/3D workspace mode normalization.
 *
 * Contract: docs/doar-core/DOARSPACE_UNIVERSAL_WORKSPACE_CONTRACT.md #6.
 * Mapping: 1D/2D/2.5D -> 2D; 3D/3.5D -> 3D; 4D -> 3D-related experience output
 * (never presented as an ordinary editable 3D scene).
 *
 * Reuses the existing alias table in core/dimensions/dimensionRegistry.js
 * (already the source AppShell uses for URL slugs/aliases) instead of
 * duplicating it, so a legacy alias only has to be taught in one place.
 */
import { normalizeDimension } from "../dimensions/dimensionRegistry";

export const WORKSPACE_MODES = Object.freeze({ TWO_D: "2D", THREE_D: "3D" });

const MODE_BY_LEGACY_DIMENSION = Object.freeze({
  "1D": WORKSPACE_MODES.TWO_D,
  "2D": WORKSPACE_MODES.TWO_D,
  "2.5D": WORKSPACE_MODES.TWO_D,
  "3D": WORKSPACE_MODES.THREE_D,
  "3.5D": WORKSPACE_MODES.THREE_D,
  "4D": WORKSPACE_MODES.THREE_D,
});

// editorKind here is the *legacy* per-dimension host component this face
// currently renders inside SpaceHost's SPACE_MAP. It is compatibility
// bookkeeping, not the future 2D/3D-workspace editor adapter id (see
// editorCapabilityRegistry.js for that).
const LEGACY_EDITOR_KIND_BY_DIMENSION = Object.freeze({
  "1D": "thought",
  "2D": "board",
  "2.5D": "motion",
  "3D": "model",
  "3.5D": "geo",
  "4D": "immersive-output",
});

/**
 * @param {unknown} rawDimension - canonical dimension, URL slug, or alias.
 * @returns {{
 *   ok: boolean,
 *   mode: "2D"|"3D"|null,
 *   legacyDimension: string|null,
 *   editorKind: string|null,
 *   isExperienceOutput: boolean,
 *   reason: string|null,
 * }}
 */
export function normalizeWorkspaceMode(rawDimension) {
  const legacyDimension = normalizeDimension(rawDimension);
  if (!legacyDimension) {
    return {
      ok: false,
      mode: null,
      legacyDimension: null,
      editorKind: null,
      isExperienceOutput: false,
      reason: "unknown_dimension",
    };
  }
  return {
    ok: true,
    mode: MODE_BY_LEGACY_DIMENSION[legacyDimension],
    legacyDimension,
    editorKind: LEGACY_EDITOR_KIND_BY_DIMENSION[legacyDimension],
    isExperienceOutput: legacyDimension === "4D",
    reason: null,
  };
}

export function isThreeDLegacyDimension(rawDimension) {
  return normalizeWorkspaceMode(rawDimension).mode === WORKSPACE_MODES.THREE_D;
}

export function isTwoDLegacyDimension(rawDimension) {
  return normalizeWorkspaceMode(rawDimension).mode === WORKSPACE_MODES.TWO_D;
}
